import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import SharedTabButton from 'shared/components/buttons/tab-button/tab-button';
import PageHeader from 'shared/components/page-header/page-header';
import PageHeaderSkeleton from 'shared/components/page-header/page-header-skeleton';
import dayjs from 'dayjs';
import SharedTable from 'shared/components/table/table';
import { IShiftActivityDao, IShiftActivityProcessType } from 'core/api/types/shift-activity.interface';
import { ShiftActivityTab, ShiftActivityTabs } from 'core/constants/shift-activity-tab';
import { AlertsApiService, ShiftActivityApiService } from 'core/api';
import { useDocument } from 'core/hooks/useDocument';
import { IAlertDao } from 'core/api/types/alerts.interface';
import { where } from 'firebase/firestore';
import { useCollection } from 'core/hooks/useCollection';
import { ISharedTableColumn, ISharedTableCustomCellTemplate } from 'shared/components/table/table-interface';
import AcknowledgeAlertDialog from 'modules/alerts/dialogs/acknowledge-alert-dialog';
import SharedButton from 'shared/components/buttons/button';
import { useDialog } from 'core/providers/DialogProvider';
import { alertTemplates } from 'modules/settings/vehicles/templates';
import { useSharedData } from 'core/providers/SharedDataProvider';
import ViewLocationDialog from './dialogs/view-location-dialog';

const AlertsTableColumns: ISharedTableColumn[] = [
  { label: 'Alert reason', key: 'reason', templateId: 'text', width: 10 },
  { label: 'Triggered at', key: 'triggeredAt', templateId: 'triggeredAt', width: 10 },
  { label: 'Acknowledged by', key: 'acknowledgedBy', templateId: 'acknowledgedBy', width: 10 },
  { label: 'Acknowledged at', key: 'acknowledgedAt', templateId: 'acknowledgedAt', width: 10 },
  { label: 'Comment', key: 'comment', templateId: 'text', width: 10 },
  { label: '', key: 'action', templateId: 'action', width: 10 },
];

const ProcessTypesTableColumns: ISharedTableColumn[] = [
  { label: 'Name', key: 'name', templateId: 'text', width: 10 },
  { label: 'Status', key: 'status', templateId: 'status', width: 10 },
];

const ShiftActivityItem = () => {
  const { shiftActivityUid } = useParams();
  const dialog = useDialog();
  const { deliveryMeta, processTypeStatuses } = useSharedData();

  const alertsConstraints = useMemo(() => [where('documentId', '==', shiftActivityUid)], [shiftActivityUid]);

  const { data: shiftActivity, loading: shiftActivityLoading } = useDocument<IShiftActivityDao>({
    apiService: ShiftActivityApiService,
    uid: shiftActivityUid,
  });

  const { tableRows, loading: alertsLoading } = useCollection<IAlertDao>({
    apiService: AlertsApiService,
    constraints: alertsConstraints,
  });

  const [selectedTab, setSelectedTab] = useState<string>(ShiftActivityTab.PROCESS_TYPES);

  const actionCellTemplate = (alert: IAlertDao) => (
    <>
      {alert.acknowledged ? null : (
        <div className='w-full flex justify-end space-x-2'>
          <SharedButton
            onClick={() => dialog?.openDialog(<AcknowledgeAlertDialog alert={alert} />)}
            type='button'
            appearance='subtle'
            spacing='compact'
            label='Acknowledge'
          />
        </div>
      )}
    </>
  );

  const processTypeStatusCellTemplate = (processType: IShiftActivityProcessType) => {
    const { status } = processType;
    let text: string = 'In Progress';
    let color: string = '#6b7280';
    if (status === 'success') {
      text = 'Complete';
      color = '#16a34a';
    } else {
      const statusLookup = processTypeStatuses?.find((pst) => pst.uid === status);
      if (statusLookup) {
        text = statusLookup.name;
        color = '#ef4444';
      } else {
        text = 'Unknown';
      }
    }
    return (
      <div
        className='w-fit px-2 py-1 rounded-lg border text-xs'
        style={{ backgroundColor: `${color}0D`, borderColor: color, color: color }}
      >
        {text}
      </div>
    );
  };

  const statusCellTemplate = (shiftActivity: IShiftActivityDao) => {
    const { status } = shiftActivity;
    let text: string = 'In Progress';
    let color: string = '#6b7280';
    if (status === 'success') {
      text = 'Complete';
      color = '#16a34a';
    } else {
      const statusLookup = deliveryMeta?.locationStatus?.find((ls) => ls.key === status);
      if (statusLookup) {
        text = statusLookup.name;
        color = '#ef4444';
      } else {
        text = 'Unknown';
      }
    }
    return (
      <div
        className='w-fit px-2 py-1 rounded-lg border text-xs'
        style={{ backgroundColor: `${color}0D`, borderColor: color, color: color }}
      >
        {text}
      </div>
    );
  };

  const customTemplates: ISharedTableCustomCellTemplate[] = [
    {
      template: processTypeStatusCellTemplate,
      id: 'status',
    },
    {
      template: actionCellTemplate,
      id: 'action',
    },
  ];

  const processTypeRows = useMemo(() => {
    return (
      shiftActivity?.processTypes.map((processType) => ({
        key: processType.uid,
        data: processType,
      })) ?? []
    );
  }, [shiftActivity]);

  const getTabContent = () => {
    switch (selectedTab) {
      case ShiftActivityTab.PROCESS_TYPES:
        return (
          <>
            <SharedTable columns={ProcessTypesTableColumns} rows={processTypeRows} customTemplates={customTemplates} />
          </>
        );
      case ShiftActivityTab.ALERTS:
        return (
          <>
            <SharedTable
              columns={AlertsTableColumns}
              rows={tableRows}
              isLoading={alertsLoading}
              customTemplates={[...customTemplates, ...alertTemplates]}
            />
          </>
        );
    }
  };

  const ShiftActivityItemDetails = ({ shiftActivity }: { shiftActivity: IShiftActivityDao }) => {
    const ShiftActivityDetails = [
      {
        label: 'Location',
        value: shiftActivity.location.name,
        key: 'location',
      },
      {
        label: 'Sub Location',
        value: shiftActivity.subLocation?.name ?? '-',
        key: 'subLocation',
      },
      {
        label: 'Notes',
        value: shiftActivity.notes ?? '-',
        key: 'notes',
      },
      {
        label: 'Status',
        value: statusCellTemplate(shiftActivity),
        key: 'status',
      },
      {
        label: 'Completed at',
        value:
          shiftActivity.status === 'success'
            ? dayjs(shiftActivity.updated.at.toDate()).format('HH:mm, DD/MM/YYYY')
            : '-',
        key: 'completedAt',
      },
      {
        label: 'Created at',
        value: shiftActivity.created.at
          ? dayjs(shiftActivity.created.at.toDate()).format('HH:mm, DD/MM/YYYY')
          : 'Unknown',
        key: 'createdAt',
      },
      {
        label: 'Updated at',
        value: shiftActivity.updated.at ? dayjs(shiftActivity.updated.at.toDate()).format('HH:mm, DD/MM/YYYY') : '-',
        key: 'updatedAt',
      },
      {
        label: 'Created by',
        value: shiftActivity.created.by.fullName ?? 'Unknown',
        key: 'createdBy',
      },
    ];

    return (
      <div className='flex-grow-0 flex-shrink-0 basis-[300px] bg-white shadow-md rounded-md h-fit'>
        <div className='border-b p-4 flex justify-between items-center'>
          <p className='headline-06'>Details</p>
          <div className='flex space-x-2'>
            {shiftActivity.submissionLocation && (
              <SharedButton
                onClick={() => dialog?.openDialog(<ViewLocationDialog position={shiftActivity.submissionLocation} />)}
                type='button'
                appearance='primary'
                label='View Location'
              />
            )}
          </div>
        </div>
        <div className='p-4'>
          {ShiftActivityDetails.map((section) => (
            <div key={section.key} className='mt-4 first:mt-0'>
              <p className='body-02 text-gray-500'>{section.label}</p>
              {section.key === 'status' ? section.value : <p className='body-01 break-words'>{section.value}</p>}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      <>
        {!shiftActivity || shiftActivityLoading ? (
          <PageHeaderSkeleton actions={[]} subtitle={true} />
        ) : (
          <>
            <PageHeader title={`${shiftActivity.location.name}`} showBack={true} />
            <div className='flex flex-row py-6 space-x-6'>
              <ShiftActivityItemDetails shiftActivity={shiftActivity} />
              <div className='flex-grow min-w-0'>
                <div className='relative'>
                  <div className='flex relative z-10'>
                    {ShiftActivityTabs.map((tab) => (
                      <SharedTabButton key={tab.key} changeTab={setSelectedTab} tab={tab} currentTab={selectedTab} />
                    ))}
                  </div>
                  <div className='absolute bottom-0 left-0 right-0 h-px bg-gray-200' />
                </div>
                <div className='mt-6'>{getTabContent()}</div>
              </div>
            </div>
          </>
        )}
      </>
    </>
  );
};

export default ShiftActivityItem;
